// NotesPage.js
import {
  Squares2X2Icon,
  GlobeAltIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { useState, useMemo, useRef, useEffect } from "react";
import CreateNoteModal from "../components/CreateNoteModal";
import { useLocation } from "../contexts/LocationContext";

// Reusable card + photo lightbox
import NoteCard from "../components/notes/NoteCard";
import PhotoLightbox from "../components/PhotoLightbox";

import FilterBar from "../ui/Filterbar";
import { PencilIcon } from "@heroicons/react/24/outline";

import useBodyScrollLock from "../hooks/useBodyScrollLock";
import useKeyScrollLock from "../hooks/useKeyScrollLock";
import useScrollLock from "../hooks/useScrollLock";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import Spinner from "../ui/Spinner";
import { useToast } from "../hooks/useToast";
import ErrorBanner from "../ui/ErrorBanner";

// Mock backend
import { useMyNotes } from "../hooks/useMyNotes";

export default function MyNotesPage() {
  const [showCreateNoteModal, setShowCreateNoteModal] = useState(false);
  const [anyMenuOpen, setAnyMenuOpen] = useState(false);

  // Photo overlay state via PhotoLightbox
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoImages, setPhotoImages] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);

  const { toast } = useToast();

  // Get user's location for note creation
  const { userLocation, requestLocation } = useLocation();

  // Request location when page mounts
  useEffect(() => {
    requestLocation();
  }, []);

  const cardsListRef = useRef(null);

  // Use mock backend hook
  const {
    notes,
    stats,
    statsLoading,
    loading,
    loadingMore,
    error,
    filter,
    setFilter,
    hasMore,
    loadMore,
    refetch,
    createNote,
    updateNote,
    deleteNote,
  } = useMyNotes("all", 20); // Load 20 notes at a time

  // Set up infinite scroll
  const sentinelRef = useInfiniteScroll(
    loadMore, // Callback to load more
    hasMore, // Whether there are more items
    loadingMore, // Whether currently loading
    0.1, // Trigger when 10% visible (loads slightly before reaching bottom)
  );

  // Use stats from backend
  const noteFilters = useMemo(() => {
    return [
      {
        key: "all",
        label: "All",
        icon: <Squares2X2Icon className="h-4 w-4" />,
        count: stats.all,
        loading: statsLoading,
      },
      {
        key: "public",
        label: "Public",
        icon: <GlobeAltIcon className="h-4 w-4" />,
        count: stats.public,
        loading: statsLoading,
      },
      {
        key: "private",
        label: "Private",
        icon: <LockClosedIcon className="h-4 w-4" />,
        count: stats.private,
        loading: statsLoading,
      },
    ];
  }, [stats, statsLoading]);

  // Locks for dropdowns/menus in NoteCard
  useScrollLock(cardsListRef, anyMenuOpen);
  useKeyScrollLock(anyMenuOpen);
  useBodyScrollLock(anyMenuOpen);

  /* Called by NoteCard to handle inline photo preview click */
  const handleRequestPhotoView = (note, index = 0) => {
    // current Note's photos
    setPhotoImages(note.images || []);
    // index to display first
    setPhotoIndex(Number.isInteger(index) ? index : 0);
    setPhotoOpen(true);
  };

  const handleClickCreate = () => {
    setShowCreateNoteModal(true);
  };

  const handleNoteCreated = (newNote) => {
    // Modal already called API and succeeded
    refetch();
  };

  return (
    <>
      {/* Filters */}
      <FilterBar
        items={noteFilters}
        value={filter}
        onChange={setFilter}
        ariaLabel="Filters"
      />

      {/* Scrollable Content */}
      <div
        ref={cardsListRef}
        className="flex-1 overflow-y-auto px-4 md:px-10 py-6 bg-surface pretty-scroll"
      >
        <div className="max-w-2xl mx-auto">
          {loading && (
            <div className="flex items-center justify-center py-10 text-text-muted">
              <div className="flex items-center gap-3">
                <Spinner size={26} className="text-text-muted/60" />
                {/* <span>Loading notes...</span> */}
              </div>
            </div>
          )}

          {error && (
            <div className="flex justify-center py-10">
              <ErrorBanner
                message={error}
                onRetry={refetch}
                dismissible={false}
              />
            </div>
          )}

          {!loading && !error && notes.length === 0 && (
            <div className="text-center py-10 text-text-muted text-sm">
              {filter === "public"
                ? "No public notes found. Create your first one!"
                : filter === "private"
                  ? "No private notes found. Create your first one!"
                  : "No notes found. Create your first note!"}
            </div>
          )}

          {!loading && !error && notes.length > 0 && (
            <>
              <ul className="space-y-6">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="rounded-2xl shadow-2xl bg-surface-elevated border border-border-light"
                  >
                    <NoteCard
                      note={note}
                      // No close button in list context
                      onRequestPhotoView={(i) =>
                        handleRequestPhotoView(note, i)
                      }
                      // In list context we don't need a menu boundary; omit to let it clamp to viewport
                      // menuBoundaryRef={cardsListRef}
                      onAnyMenuOpenChange={setAnyMenuOpen}
                      isOwner={note.isOwner}
                      onNoteDeleted={deleteNote}
                      onNoteUpdated={refetch}
                    />
                  </div>
                ))}
              </ul>

              {/* Sentinel element for infinite scroll */}
              <div
                ref={sentinelRef}
                className="h-20 flex items-center justify-center"
              >
                {loadingMore && (
                  <div className="flex items-center gap-2 text-text-muted">
                    <Spinner size={20} />
                    <span className="text-sm">Loading more notes...</span>
                  </div>
                )}
                {!loadingMore && !hasMore && notes.length > 0 && (
                  <p className="text-sm text-text-muted">
                    You've reached the end
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dedicated Create Note Button */}
      <button
        onClick={handleClickCreate}
        className="fixed bottom-28 md:bottom-10 right-2 md:right-10 z-30 h-12 w-12 rounded-full grid place-items-center bg-surface-elevated border border-border shadow-lg hover:bg-surface active:bg-surface-sunken"
        aria-label="Create a new note"
      >
        <PencilIcon className="h-5 w-5 text-primary" />
      </button>

      {/* Create Note modal */}
      {showCreateNoteModal && (
        <CreateNoteModal
          onClose={() => setShowCreateNoteModal(false)}
          onSuccess={handleNoteCreated}
          initialCoordinates={{
            latitude: userLocation?.latitude,
            longitude: userLocation?.longitude,
          }}
          createNote={createNote}
        />
      )}

      {/* Page-level PhotoLightbox overlay (opened from any NoteCard) */}
      {photoOpen && (
        <PhotoLightbox
          open={photoOpen}
          images={photoImages}
          index={photoIndex}
          onIndexChange={setPhotoIndex}
          onClose={() => setPhotoOpen(false)}
        />
      )}
    </>
  );
}
